import { createRpcBackend } from "@/backend/backendProvider";

import { ensureSuccessResponse } from "./remoteCaller";

const pathValidationMap = new Map();

export function almHandlerAdo(remote, connectionId, almSessionId) {
  const backend = createRpcBackend(remote.context);

  function getIssueTypes() {
    return backend
      .almToolCall("ado", "get_work_item_types", [connectionId])
      .then((almIssueTypes) => {
        ensureSuccessResponse(almIssueTypes);
        return almIssueTypes.map((almIssueType) => ({
          id: almIssueType.referenceName,
          name: almIssueType.name,
        }));
      });
  }

  return {
    name: "Azure DevOps",
    supportsLinks: true,
    checkStatus: () => {
      return Promise.resolve({ working: true });
    },
    getIssueLinkTypes: () => {
      return backend.almToolCall("ado", "get_workitem_link_types", [connectionId]).then((links) => {
        return links.map((l) => ({
          link_type: l.name,
          issue_link_id: l.referenceName,
          name: l.name,
        }));
      });
    },
    getMapping: () => {
      return backend.almToolCall("ado", "get_team_mapping", [connectionId]).then((mappings) =>
        mappings.map((mapping) => ({
          user_id: mapping.team_id,
          sources: mapping.sources.map((m) => ({
            id: m.id,
            areaPath: m.area_path,
          })),
        })),
      );
    },
    getProjects: () => {
      return backend
        .almToolCall("ado", "get_projects", [connectionId])
        .then((projects) => projects.map((project) => ({ id: project.id, name: project.name })));
    },
    getProject: () => {
      return backend.almToolCall("ado", "get_project", [], { connection_id: connectionId });
    },
    setProject: (projectId) => {
      return backend.almToolCall("ado", "set_project", [connectionId, projectId]);
    },
    setPat: (pat) => {
      return backend.almToolCall("ado", "set_pat", [], {
        pat,
        connection_id: connectionId,
      });
    },
    getWebhookUrl: () => {
      return backend.almToolCall("ado", "get_webhook_endpoint", [], {
        connection_id: connectionId,
      });
    },
    getIssueTypes,
    getPortfolioIssueTypes: getIssueTypes,
    getIssueFields(issueType) {
      return backend.almToolCall("ado", "get_work_item_fields", [], {
        connection_id: connectionId,
        ref_name: issueType,
      });
    },
    checkLinkType: () => {
      return Promise.resolve({ from_errors: [], to_errors: [] });
    },
    getAlmTypes: () => {
      // get_alm_types is rawResponse (PI-86): the opaque snake_case ado_* keys
      // survive untransformed, so the mapping below is unchanged.
      return backend.almSyncCall("ado", "session.get_alm_types", [almSessionId]).then((almTypes) =>
        almTypes.map((almType) => ({
          id: almType.id,
          issue_type: almType.ado_work_item_type,
          project_id: null,
          wsjf: () => almType.ado_wsjf_field,
          storyPoints: almType.ado_sp_field,
          fields: (almType.ado_fields || []).map((field) => ({
            type: { key: field.referenceName, operation: field.operator },
            value: field.value,
            error: null,
            validating: false,
          })),
        })),
      );
    },
    getTeamBoardAlmType: (almType) => {
      return almType
        ? {
            issueType: almType.issue_type,
            storyPointField: almType.storyPoints,
          }
        : {};
    },
    getBacklogBoardAlmType: (_, almType) => {
      return almType ? { selectedField: almType.wsjf(), adoFields: almType.fields } : {};
    },
    getLinkTypes: () => {
      return backend
        .almSyncCall("ado", "session.get_link_types", [almSessionId])
        .then((almLinkTypes) =>
          almLinkTypes.map((almLinkType) => ({
            pi_link_type_id: almLinkType.pi_link_type_id,
            issue_link_id: almLinkType.ado_relation,
            id: almLinkType.id,
            type: almLinkType.ado_relation_name,
          })),
        );
    },
    deleteLinkType: (link) => {
      // WAMP passes only the link-type id; the alm-sync session id is threaded
      // out-of-band for the NATS subject (ignored on the WAMP wire).
      return backend.almSyncCall(
        "ado",
        "session.delete_link_type",
        [link.almId],
        undefined,
        almSessionId,
      );
    },
    updateLinkType: (link) => {
      return backend.almSyncCall(
        "ado",
        "session.update_link_type",
        [
          link.almId,
          {
            ado_relation: link.type.issue_link_id,
            ado_relation_name: link.type.link_type,
          },
        ],
        undefined,
        almSessionId,
      );
    },
    createLinkType: (link) => {
      // This ALM mapping call uses the legacy WAMP payload: existing PI link
      // type id plus selected ADO relation metadata. NATS ALM-sync create currently
      // has a different from/to-sticky contract, so SwitchingRpcBackend falls
      // back to WAMP for this method.
      return backend.almSyncCall(
        "ado",
        "session.create_link_type",
        [almSessionId, link.id, link.type.issue_link_id, link.type.link_type],
        undefined,
        almSessionId,
      );
    },
    getTags: () => {
      return Promise.resolve([]);
    },
    /**
     * Makes a request to validate the given area or iteration path.
     * Returns a promise that resolves to an object indicating success or failure.
     * The request is cached (by path and connectionId) to avoid redundant calls.
     *
     * @param {Object} path - The path object containing either an area or iteration.
     * @returns {Promise<Object>} - A promise that resolves to an object with a success flag
     * and optionally a value or error message.
     */
    validatePath(path) {
      // Pick the path type by which key is present, not by truthiness: an empty
      // area must not fall through to the iteration branch (which then sends an
      // undefined iteration_path and the BE rejects the missing arg).
      const name = "area" in path ? "area" : "iteration";
      const value = path[name];
      if (!value) {
        return Promise.resolve({
          success: false,
          error: `${name === "area" ? "Area" : "Iteration"} path is required.`,
        });
      }
      const param = {
        connection_id: connectionId,
        [name + "_path"]: value,
      };
      const pathKey = JSON.stringify(param);
      if (!pathValidationMap.has(pathKey)) {
        pathValidationMap.set(pathKey, this._validatePath({ name, param }));
      }

      return pathValidationMap.get(pathKey);
    },
    async _validatePath({ name, param }) {
      const res = await backend.almToolCall("ado", `validate_${name}_path`, [], param);
      if (typeof res === "object" && "success" in res) {
        return res;
      }
      return res
        ? { success: true, value: res }
        : { success: false, error: `Invalid ${name} path` };
    },
    loadSourceSprints: (source) => {
      source.error = null;
      source.validating = false;
      return Promise.resolve();
    },
    setSourceSprint: async (source, sessionSource) => {
      source.iterationPath = (sessionSource.iteration_path || sessionSource.iterationPath) ?? null;
    },
    async loadAlmItemTypes(dry_run = false) {
      return await backend.almSyncCall("ado", "item.load_alm_item_types", [connectionId], {
        dry_run,
      });
    },
  };
}
